import React from "react";
import HashTag from "./HashTag";

/**
 * A tiny, dependency-free markdown renderer for note bodies.
 *
 * Supported syntax:
 *   ## Heading            → section heading
 *   ### Smaller heading
 *   - bullet              → unordered list (consecutive lines)
 *   **bold**  *italic*  `code`
 *   [text](https://url)   → link
 *   #Hashtag              → auto-linked cross-reference (single token, no spaces)
 *   blank line            → new paragraph
 */

const INLINE =
  /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))|(#([A-Za-z0-9_]+))/g;

function renderInline(text, keyBase) {
  const nodes = [];
  let last = 0;
  let m;
  let i = 0;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const key = `${keyBase}-${i++}`;
    if (m[2] !== undefined) {
      nodes.push(<strong key={key} className="text-foreground font-semibold">{m[2]}</strong>);
    } else if (m[4] !== undefined) {
      nodes.push(<em key={key}>{m[4]}</em>);
    } else if (m[6] !== undefined) {
      nodes.push(
        <code key={key} className="px-1.5 py-0.5 rounded bg-surface-elevated text-foreground text-[0.9em] border border-border">
          {m[6]}
        </code>
      );
    } else if (m[8] !== undefined) {
      nodes.push(
        <a key={key} href={m[9]} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent-hover underline-offset-2 hover:underline">
          {m[8]}
        </a>
      );
    } else if (m[11] !== undefined) {
      nodes.push(<HashTag key={key} tag={m[11]} />);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export default function MarkdownLite({ source = "", className = "" }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let list = null;
  let k = 0;

  const flushList = () => {
    if (list) {
      blocks.push(
        <ul key={`ul-${k++}`} className="my-4 list-disc pl-6 space-y-1.5 text-muted">
          {list.map((item, idx) => (
            <li key={idx}>{renderInline(item, `li-${k}-${idx}`)}</li>
          ))}
        </ul>
      );
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === "") {
      flushList();
      continue;
    }
    if (line.startsWith("- ")) {
      (list ||= []).push(line.slice(2));
      continue;
    }
    flushList();
    if (line.startsWith("### ")) {
      blocks.push(
        <h4 key={`h-${k++}`} className="font-display text-lg font-semibold text-foreground mt-6 mb-2">
          {renderInline(line.slice(4), `h${k}`)}
        </h4>
      );
    } else if (line.startsWith("## ")) {
      blocks.push(
        <h3 key={`h-${k++}`} className="font-display text-xl font-semibold text-foreground mt-8 mb-3">
          {renderInline(line.slice(3), `h${k}`)}
        </h3>
      );
    } else {
      blocks.push(
        <p key={`p-${k++}`} className="my-4 leading-relaxed text-muted">
          {renderInline(line, `p${k}`)}
        </p>
      );
    }
  }
  flushList();

  return <div className={className}>{blocks}</div>;
}
